import React, {
  useImperativeHandle,
  useCallback,
  useEffect,
  useRef,
  useLayoutEffect
} from "react";
import PropTypes from "kf-proptypes/widgets";

import { useAccountStore } from "base/stores";
import { getS3FileKey } from "base/utils";
import { FILE_PREVIEW_EXTENSIONS, FILEPREVIEW_PLUGINS } from "base/constants";

import { LazyLoadComponent } from "@widgets/others/lazyloader/index.jsx";
import { Tooltip } from "../../feedback/tooltip/index.jsx";
import { FileIcon } from "../../common/components/primitives/fileicon/index.jsx";
import { CollageContainer } from "./collage.jsx";

import styles from "./collage.css";

const IMAGEBASEURL = `/upload/2`;

Collage.propTypes = {
  node: PropTypes.shape({
    files: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        height: PropTypes.number,
        width: PropTypes.number,
        fileExtension: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        size: PropTypes.number.isRequired,
        duration: PropTypes.number
      })
    ),
    images: PropTypes.arrayOf(
      PropTypes.shape({
        default: PropTypes.shape({
          key: PropTypes.string.isRequired,
          height: PropTypes.number,
          width: PropTypes.number,
          fileExtension: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          size: PropTypes.number.isRequired,
          duration: PropTypes.number
        }).isRequired,
        preview: PropTypes.shape({
          key: PropTypes.string.isRequired,
          height: PropTypes.number,
          width: PropTypes.number,
          fileExtension: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          size: PropTypes.number.isRequired,
          duration: PropTypes.number
        }).isRequired,
        collage: PropTypes.shape({
          key: PropTypes.string.isRequired,
          height: PropTypes.number,
          width: PropTypes.number,
          fileExtension: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          size: PropTypes.number.isRequired,
          duration: PropTypes.number
        }).isRequired,
        thumbnail: PropTypes.shape({
          key: PropTypes.string.isRequired,
          height: PropTypes.number,
          width: PropTypes.number,
          fileExtension: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          size: PropTypes.number.isRequired,
          duration: PropTypes.number
        }).isRequired,
        original: PropTypes.shape({
          key: PropTypes.string.isRequired,
          height: PropTypes.number,
          width: PropTypes.number,
          fileExtension: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          size: PropTypes.number.isRequired,
          duration: PropTypes.number
        }).isRequired,
        isPublic: PropTypes.bool.isRequired,
        src: PropTypes.string.isRequired,
        thumbnailSrc: PropTypes.string.isRequired,
        caption: PropTypes.string.isRequired,
        id: PropTypes.number
      })
    )
  }).isRequired,
  layout: PropTypes.arrayOf(PropTypes.number),
  height: PropTypes.arrayOf(PropTypes.string),
  innerRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }).isRequired,
  readOnly: PropTypes.bool,
  className: PropTypes.string,
  fileClassName: PropTypes.string,
  borderedImageRadius: PropTypes.bool,
  tileClassname: PropTypes.string,
  columnImage: PropTypes.bool,
  columnImageWidth: PropTypes.string,
  flowType: PropTypes.string,
  messageId: PropTypes.string,
  roomId: PropTypes.string,
  updateAnnotatedAttachments: PropTypes.func
};

export function Collage(props) {
  const {
    node,
    layout,
    height,
    innerRef,
    readOnly,
    className = "",
    fileClassName = "",
    borderedImageRadius = false,
    tileClassname,
    columnImage = false,
    columnImageWidth,
    flowType,
    messageId,
    roomId,
    updateAnnotatedAttachments = () => {}
  } = props;
  let fileList = node.files || [];
  const filePreviewRef = useRef();

  useLayoutEffect(function filePreviewEffect() {
    async function importFilePreview() {
      let { filePreviewAPI } = await import("kfplatform/filePreview");
      filePreviewRef.current = filePreviewAPI;
    }

    importFilePreview();
  }, []);

  let nodeFile = [];
  let gifFile = [];
  const videoFile = [];
  const audioFile = [];

  fileList.forEach((file) => {
    const type = file.fileExtension || "";
    if (FILE_PREVIEW_EXTENSIONS.VIDEO_FORMATS.includes(type.toLowerCase())) {
      videoFile.push(file);
    } else if (
      FILE_PREVIEW_EXTENSIONS.AUDIO_FORMATS.includes(type.toLowerCase())
    ) {
      audioFile.push(file);
    } else if (file.fileExtension === "gif") {
      gifFile.push({
        ...file,
        thumbnail: {
          key: `${file.key}?`
        }
      });
    } else {
      nodeFile.push(file);
    }
  });

  let attachmentFileList = [...nodeFile];
  let imageList = getImages([...node.images, ...gifFile]);

  let previewImageList = imageList.map((image) => ({
    fileExtension: image.preview?.fileExtension || image.fileExtension,
    key: image.original?.key || image.key,
    name: image.preview?.name || image.name,
    isPublic: image.isPublic,
    preview: image.preview?.key || image.key,
    thumbnail: image.thumbnail?.key || image.key
  }));

  const openPreview = useCallback(function openPreviewCallback(fileid) {
    const allFiles = [
      ...videoFile,
      ...audioFile,
      ...previewImageList,
      ...attachmentFileList
    ];
    filePreviewRef.current.open({ files: allFiles });
    const fileIndex = allFiles.findIndex((file) => file.key === fileid);
    filePreviewRef.current.setFileIndex(parseInt(fileIndex, 10));
    filePreviewRef.current.openVideoPlayerPreview(true);
    if (flowType === "chat") {
      filePreviewRef.current.setPlugins([
        FILEPREVIEW_PLUGINS.ANNOTATE,
        FILEPREVIEW_PLUGINS.DOWNLOAD,
        FILEPREVIEW_PLUGINS.PRINT,
        FILEPREVIEW_PLUGINS.NEWTAB,
        FILEPREVIEW_PLUGINS.FULLSCREEN
      ]);
      filePreviewRef.current.annotation(
        {
          isSavingAnnotatedFileOnNewPath: true,
          uploadPrefixURL: `chat/${roomId}/Files`
        },
        (fileObj) => {
          updateAnnotatedAttachments(fileObj);
        }
      );
      filePreviewRef.current.setNewTabURL(
        `/view/filepreview/${flowType}/${roomId}/${messageId}`
      );
    } else {
      filePreviewRef.current.setPlugins([
        FILEPREVIEW_PLUGINS.DOWNLOAD,
        FILEPREVIEW_PLUGINS.PRINT,
        FILEPREVIEW_PLUGINS.FULLSCREEN
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function filePreviewEffect() {
    return () => {
      if (filePreviewRef.current?.openPreview) {
        filePreviewRef.current.close();
      }
    };
  }, []);

  useImperativeHandle(innerRef, () => {
    return {
      getData: () => {
        return node;
      }
    };
  });

  function getImages(images = []) {
    const allImages = [];
    const PUBLICURL = useAccountStore.getState().AppConfig?.ProfileBasePath;
    images.forEach((img) => {
      const data = { ...img };
      data.src = `${img?.isPublic ? PUBLICURL : IMAGEBASEURL}/${getS3FileKey(
        data.preview?.key || data.key
      )}`;
      data.thumbnailSrc = `${
        img?.isPublic ? PUBLICURL : IMAGEBASEURL
      }/${getS3FileKey(data.default?.key || data.key, roomId)}`;
      data.caption = data.default?.name || data.name;
      allImages.push(data);
    });
    return allImages;
  }

  return (
    <div className={className}>
      {videoFile.map((file) => {
        return (
          <div
            className={`videoPlayerWrapper ${styles.player}`}
            key={file.index}
          >
            <LazyLoadComponent
              component={() =>
                import(/* webpackPrefetch: true */ "kfplatform/filePreview")
              }
              componentName="VideoPlayer"
              file={file}
              onOpenPreview={() => openPreview(file.key)}
            />
          </div>
        );
      })}
      {audioFile.map((file) => {
        return (
          <div className={`audioPlayerWrap ${styles.player}`} key={file.index}>
            <LazyLoadComponent
              component={() =>
                import(/* webpackPrefetch: true */ "kfplatform/filePreview")
              }
              componentName="AudioPlayer"
              file={file}
              onOpenPreview={() => openPreview(file.key)}
            />
          </div>
        );
      })}
      {imageList && imageList.length > 0 && (
        <CollageContainer
          images={imageList}
          layout={layout}
          height={height}
          borderedImageRadius={borderedImageRadius}
          openPreview={openPreview}
          tileClassname={tileClassname}
          columnImage={columnImage}
          columnImageWidth={columnImageWidth}
        />
      )}
      {fileList.length > 0 && (
        <div
          className={`${styles.attachmentscontainer} ${fileClassName} ${
            readOnly ? styles.readonlyFileWrapper : styles.fileWrapper
          }`}
        >
          {attachmentFileList.map((file) => {
            return (
              <React.Fragment key={file.index}>
                <div
                  className={styles.fileContainer}
                  onClick={() => openPreview(file.key)}
                >
                  {file.fileExtension &&
                    !["gif"].includes(file.fileExtension) && (
                      <Tooltip content={file.name}>
                        <div className={styles.svgContainer}>
                          <FileIcon type={file.fileExtension} />
                        </div>
                      </Tooltip>
                    )}
                  {file.fileExtension &&
                    ["gif"].includes(file.fileExtension) && (
                      <div className={styles.gifImageContainer}>
                        <img
                          id="1"
                          alt="gif"
                          src={`${IMAGEBASEURL}/${getS3FileKey(
                            file.key,
                            roomId
                          )}`}
                        />
                      </div>
                    )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
